import React, { useEffect, useRef, useState } from "react";
import styles from "./slides.module.scss";
import GradientCursorFollower from "@/components/layout/gradient-cursor-follow/gradient-cursor-follower";
import Link from "next/link";
import SingleLineBackground from "@/components/layout/lines-background/single-line";
import ArrowButtonRight from "@/components/layout/arrow-button/arrow-button-right";
import ArrowButtonLeft from "@/components/layout/arrow-button/arrow-button-left";
import dayjs from "dayjs";
import { getPostUpdates, Post } from "@/services/api/cms/get-post-updates";

export interface UpdateType {
  title: string;
  date: string;
}

const CARDS: UpdateType[] = [
  { title: "Launching New Features for Enhanced User Experience", date: "2024-09-01" },
  { title: "Upcoming Maintenance: Expect Downtime", date: "2024-09-15" },
  { title: "Introducing New Analytics Dashboard", date: "2024-10-05" },
  { title: "March Release: Improved Performance and Stability", date: "2025-03-30" },
  { title: "Launching New Features for Enhanced User Experience", date: "2024-09-01" },
  { title: "Upcoming Maintenance: Expect Downtime", date: "2024-09-15" },
  { title: "Introducing New Analytics Dashboard", date: "2024-10-05" },
  { title: "March Release: Improved Performance and Stability", date: "2025-03-30" },
];

const Slides = () => {
  const [posts, setPosts] = useState<Post[]>([]);

  const [xPos, setXPos] = useState<number>(0);
  const scrolledCardCount = useRef<number>(0);
  const sliderContainer = useRef<HTMLDivElement>(null);
  const sliderRef = useRef<HTMLDivElement>(null);
  const cardRef = useRef<HTMLDivElement>(null);

  const handleNext = () => {
    // If the slider end x position is less than the width of the screen
    if (sliderRef.current && sliderRef.current?.getBoundingClientRect().right < sliderContainer.current?.getBoundingClientRect().right!) return;
    moveCards("right");
  };

  function moveCards(direction: "left" | "right") {
    if (direction === "left") {
      setXPos((prev) => prev + cardRef.current?.clientWidth! + parseInt(styles.cardGap));
      scrolledCardCount.current--;
    }
    if (direction === "right") {
      setXPos((prev) => prev - cardRef.current?.clientWidth! - parseInt(styles.cardGap));
      scrolledCardCount.current++;
    }
  }

  const handlePrev = () => {
    if (xPos >= 0) return;
    if (cardRef.current) moveCards("left");
  };

  useEffect(() => {
    const observer = new ResizeObserver((entries) => {
      const entry = entries[0];
      const cardWidth = entry.target.clientWidth;
      const sliderPos = sliderRef.current?.getBoundingClientRect().right!;
      const containerRight = sliderContainer.current?.getBoundingClientRect().right!;
      const diff = containerRight - sliderPos;
      const cardGap = parseInt(styles.cardGap);
      const cardsToMove = Math.floor(diff / (cardWidth + cardGap));

      if (cardsToMove >= 1) {
        const position = (cardWidth + cardGap) * scrolledCardCount.current;
        setXPos(-position);
      } else {
        // Scroll to the current resized card again
        const position = (cardWidth + cardGap) * scrolledCardCount.current;
        setXPos(-position);
      }

      return () => {
        if (cardRef.current) {
          observer.unobserve(cardRef.current);
        }
      };
    });

    if (cardRef.current) {
      observer.observe(cardRef.current);
    }
  }, []);

  useEffect(() => {
    (async () => {
      const posts = await getPostUpdates();
      setPosts(posts);
    })();
  }, []);

  return (
    <>
      <SingleLineBackground />
      <div className={styles.sliderContainer} ref={sliderContainer}>
        <div className={styles.slider} ref={sliderRef} style={{ transform: `translateX(${xPos}px)` }}>
          {posts?.map((post, i) => {
            return (
              <Link href={`/updates/${post.slug}`} className={styles.linkCardContainer} key={post.slug}>
                <div ref={cardRef} className={styles.card}>
                  <GradientCursorFollower />
                  <h4>{post.title}</h4>
                  <span>{dayjs(post.published_at as string).format("MMM D YYYY")}</span>
                  <p>{post.excerpt}</p>
                </div>
              </Link>
            );
          })}
        </div>
        <div className={styles.buttons}>
          <ArrowButtonLeft onClick={handlePrev} />
          <ArrowButtonRight onClick={handleNext} />
        </div>
      </div>
    </>
  );
};

export const getStaticProps = async () => {
  // Call an external API endpoint to get posts.
  // You can use any data fetching library
  const posts = await getPostUpdates();

  // By returning { props: { posts } }, the Blog component
  // will receive `posts` as a prop at build time
  return {
    props: {
      posts,
    },
  };
};

export default Slides;
